/**
 * ResourceClickTracker
 * 
 * Handles click tracking for resource action buttons (Download and View Link)
 * Sends AJAX request to create click event before navigating to the resource
 * 
 * Usage:
 * - Add class "resource-click-tracker" to action buttons (both download and view link)
 * - Add data-resource-id attribute with resource ID
 * - Add data-click-count-selector attribute with selector for click count display element
 */
class ResourceClickTracker {
  constructor() {
    this.initialized = false;
    this.eventHandlers = new Map(); // Track handlers to prevent duplicates
    this.init();
  }

  /**
   * Initialize the click tracker
   * Sets up event listeners for all resource action buttons
   */
  init() {
    // Clean up existing handlers first
    this.cleanup();

    const actionButtons = document.querySelectorAll('.resource-click-tracker');
    
    actionButtons.forEach(button => {
      // Skip if already has handler
      if (this.eventHandlers.has(button)) {
        return;
      }

      const handler = this.handleClick.bind(this);
      this.eventHandlers.set(button, handler);
      button.addEventListener('click', handler);
    });

    this.initialized = true;
  }

  /**
   * Clean up event listeners
   */
  cleanup() {
    this.eventHandlers.forEach((handler, button) => {
      button.removeEventListener('click', handler);
    });
    this.eventHandlers.clear();
    this.initialized = false;
  }

  /**
   * Handle click event on resource action button (download or view link)
   * @param {Event} event - Click event
   */
  handleClick(event) {
    const button = event.currentTarget;
    
    // Prevent double-firing by checking if already processing
    if (button.dataset.tracking === 'processing') {
      event.preventDefault();
      return;
    }

    const resourceId = button.getAttribute('data-resource-id');
    const clickCountSelector = button.getAttribute('data-click-count-selector');
    
    // Use numeric ID for server-side resource identification
    const identifier = resourceId;
    
    if (!identifier) {
      console.warn('ResourceClickTracker: No resource ID or slug found on button');
      return;
    }

    // Mark as processing to prevent double-firing
    button.dataset.tracking = 'processing';

    // Prevent default navigation temporarily
    event.preventDefault();
    event.stopPropagation();
    
    // Get the original href, target, and download attributes
    const href = button.getAttribute('href');
    const target = button.getAttribute('target');
    const download = button.getAttribute('download');
    
    // Send AJAX request to create click event
    this.incrementClick(identifier, clickCountSelector, () => {
      // Reset processing flag
      delete button.dataset.tracking;
      
      // After successful click event creation, proceed with navigation
      if (download) {
        // For downloads, create a temporary link and click it
        const link = document.createElement('a');
        link.href = href;
        link.download = download;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (target === '_blank') {
        window.open(href, target);
      } else {
        window.location.href = href;
      }
    }, () => {
      // Reset processing flag on error
      delete button.dataset.tracking;
    });
  }

  /**
   * Send AJAX request to increment click counter
   * @param {string} resourceIdentifier - Resource ID or slug
   * @param {string} clickCountSelector - CSS selector for click count display element
   * @param {Function} callback - Callback function to execute after successful increment
   * @param {Function} errorCallback - Callback function to execute on error
   */
  incrementClick(resourceIdentifier, clickCountSelector, callback, errorCallback) {
    const url = `/resources/${resourceIdentifier}/increment_click`;
    const csrfToken = this.getCSRFToken();
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        // Update click count display if selector provided
        if (clickCountSelector) {
          this.updateClickCountDisplay(clickCountSelector, data.click_count);
        }
        
        // Execute callback to proceed with navigation
        if (callback) {
          callback();
        }
      } else {
        console.error('ResourceClickTracker: Failed to increment click', data.error);
        // Still proceed with navigation even if tracking fails
        if (callback) {
          callback();
        }
      }
    })
    .catch(error => {
      console.error('ResourceClickTracker: Error incrementing click', error);
      // Still proceed with navigation even if tracking fails
      if (errorCallback) {
        errorCallback();
      }
      if (callback) {
        callback();
      }
    });
  }

  /**
   * Update click count display element
   * @param {string} selector - CSS selector for the element (ID without #)
   * @param {number} count - New click count
   */
  updateClickCountDisplay(selector, count) {
    // Remove # if present
    const cleanSelector = selector.replace(/^#/, '');
    const element = document.getElementById(cleanSelector);
    
    if (element) {
      if (count > 0) {
        const pluralized = count === 1 ? 'download' : 'downloads';
        // Check if element is in search results (inline) or category page (block)
        const isInline = element.closest('.resource-search-result-meta');
        
        if (isInline) {
          element.textContent = ` • ${count} ${pluralized}`;
        } else {
          element.textContent = `${count} ${pluralized}`;
        }
        element.style.display = '';
      } else {
        element.textContent = '';
        element.style.display = 'none';
      }
    }
  }

  /**
   * Get CSRF token from meta tag
   * @returns {string} CSRF token
   */
  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    return metaTag ? metaTag.getAttribute('content') : '';
  }
}

// Singleton instance (expose to window for re-initialization)
let trackerInstance = null;
window.trackerInstance = trackerInstance;

/**
 * Initialize tracker (prevents multiple instances)
 */
function initializeTracker() {
  // Clean up existing instance if it exists
  if (trackerInstance) {
    trackerInstance.cleanup();
  }
  
  // Create new instance
  trackerInstance = new ResourceClickTracker();
  window.trackerInstance = trackerInstance;
}

// Use a flag to prevent multiple simultaneous initializations
let isInitializing = false;

function safeInitializeTracker() {
  if (isInitializing) {
    return;
  }
  
  isInitializing = true;
  
  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    initializeTracker();
    isInitializing = false;
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInitializeTracker);
} else {
  safeInitializeTracker();
}

// Re-initialize on Turbo navigation (if using Turbo)
if (typeof Turbo !== 'undefined') {
  document.addEventListener('turbo:load', safeInitializeTracker);
  document.addEventListener('turbo:render', safeInitializeTracker);
}

// Expose safeInitializeTracker for external use
window.safeInitializeClickTracker = safeInitializeTracker;


